import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete, Box, Button, IconButton, List, ListItem, ListItemText, TextField } from '@mui/material';
import React, { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Form, useLoaderData, useSubmit } from 'react-router-dom';
import { axiosApi } from '../../interceptors/axios';
import { Preference } from '../prefence/type';
import { UserPreference } from '../userPreference/type';

interface UserPreferenceCreateDto {
    orderNo: number;
    priorityOrder: number;
}

const ItemType = 'PREFERENCE';

interface DraggableItemProps {
    index: number;
    moveItem: (dragIndex: number, hoverIndex: number) => void;
    modifiedPreferences: UserPreference[]
    setModifiedPreferences: React.Dispatch<React.SetStateAction<UserPreference[]>>
}

const DraggableItem: React.FC<DraggableItemProps> = ({ index, moveItem, modifiedPreferences, setModifiedPreferences }) => {
    const { preferenceList } = useLoaderData() as { preferenceList: Preference[], userPreferenceList: UserPreference[] }
    const [, ref] = useDrag({
        type: ItemType,
        item: { index },
        canDrag: !isNaN(modifiedPreferences[index].orderNo)
    }, [index, modifiedPreferences]);

    const handleSelectChange = (event: React.SyntheticEvent, value: Preference | null, index: number) => {
        const updatedPreferences = [...modifiedPreferences];
        if (value) {
            updatedPreferences[index] = {
                ...updatedPreferences[index],
                preference: value,
                orderNo: value.orderNo,
                assignmentPeriodCode: value.assignmentPeriodCode,
                priorityOrder: index,
            };
            setModifiedPreferences(updatedPreferences);
        }
    };

    const handleDelete = (index: number) => {
        const updatedPrefs = modifiedPreferences.filter((_, i) => i !== index);
        const updatedWithOrder = updatedPrefs.map((pref, i) => ({
            ...pref,
            priorityOrder: i + 1, // Silinen tercih sonrası sıralama güncelleme
        }));
        setModifiedPreferences(updatedWithOrder);
    };
    const [, drop] = useDrop({
        accept: ItemType,
        drop: (draggedItem: { index: number }) => {
            moveItem(draggedItem.index, index);
        },
    }, [index, modifiedPreferences]);

    return (
        <ListItem
            ref={(node) => ref(drop(node))}
            secondaryAction={
                modifiedPreferences[index]?.preference ? (
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(index)}>
                        <DeleteIcon />
                    </IconButton>
                ) : null
            }
        >
            <ListItemText
                sx={{ textWrap: "nowrap", mr: 2 }}
                primary={`${index + 1}. Tercih`}
            />
            <Autocomplete
                value={preferenceList.find(d => d.orderNo === modifiedPreferences[index]?.orderNo) || null}
                fullWidth
                options={preferenceList}
                autoHighlight
                onChange={(e, v) => handleSelectChange(e, v, index)}
                renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                        <Box
                            key={key}
                            component="li"
                            {...optionProps}
                        >

                            {option.orderNo}. {option.unitName} ({option.availableSlots})
                        </Box>
                    );
                }}
                getOptionDisabled={(p => modifiedPreferences.some(
                    (pref) => pref.orderNo === p?.orderNo
                ))}
                getOptionLabel={p => p.unitName}
                renderInput={(params) => {
                    const { inputProps, InputLabelProps, InputProps, ...paramRest } = params;

                    return <TextField
                        {...paramRest}
                        label={"Tercih"}
                        fullWidth
                        slotProps={{
                            input: {
                                ...InputProps,
                                inputProps: inputProps,
                            },
                            inputLabel: InputLabelProps
                        }}
                    />
                }
                }
            />
        </ListItem>
    );
};

const PreferencesPage = () => {
    const { userPreferenceList } = useLoaderData() as { preferenceList: Preference[], userPreferenceList: UserPreference[] }
    const [modifiedPreferences, setModifiedPreferences] = useState<UserPreference[]>(function init() {
        return Array.from({ length: 10 }).map((_, i) => userPreferenceList.find(d => d.priorityOrder === i) || { priorityOrder: i } as UserPreference)
    });
    const submit = useSubmit();
    const handleSave = async () => {
        const saveData: UserPreferenceCreateDto[] = modifiedPreferences.map((pref, index) => ({
            orderNo: pref.orderNo,
            priorityOrder: index + 1,
        }));

        await axiosApi.post('/api/userPreferences/save', { userPreferenceCreateDtoList: saveData });
        alert('Tercihler kaydedildi');
    };
    const moveItem = (fromIndex: number, toIndex: number) => {
        const items = Array.from(modifiedPreferences);
        const [reorderedItem] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, reorderedItem);

        const updatedWithOrder = items.map((pref, i) => ({
            ...pref,
            priorityOrder: i + 1,
        }));
        setModifiedPreferences(updatedWithOrder);
    };

    return (
        <Form method='post'>
            <DndProvider backend={HTML5Backend}>
                <h1>Tercihler</h1>
                <List >
                    {modifiedPreferences.map((up, index) => <DraggableItem
                        key={up.priorityOrder}
                        index={index}
                        moveItem={moveItem}
                        modifiedPreferences={modifiedPreferences}
                        setModifiedPreferences={setModifiedPreferences}
                    />)}

                </List>

                <Button variant="contained" color="primary" onClick={e => {
                    const formData = new FormData();
                    formData.append("preferences", JSON.stringify(modifiedPreferences.filter(d => !isNaN(d.orderNo))));
                    submit(formData, { method: "post" })
                }}>
                    Kaydet
                </Button>
            </DndProvider>
        </Form>
    );
};

export default PreferencesPage;
